import { useState, type ReactNode } from 'react';
import type { AgentDataBlock } from '@forecastify/shared';
import { DataList, Progress, Tooltip, type SortState } from '@forecastify/ui';
import { useDuration } from '../../duration';
import { useI18n, useT, type Translate } from '../../i18n';
import { listLabels } from '../delivery/clients-screen';
import { CHAT_TASK_COLUMNS, taskColumns, taskRows } from '../tasks/task-columns';
import { useAvatars } from '../use-avatar';
import { CHAT_WORKLOG_COLUMNS, worklogColumns, worklogRows } from '../time/worklog-columns';
import { dayOfMonth, dowLabel } from '../time/week';
import { byCell, sortInMemory } from '../sort-in-memory';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C27.2 · a structured result, in a reply. `docs/plan/agent.md` §4 · R-26   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EVERY ROW HERE ARRIVED ON A `data` FRAME THAT A TOOL EMITTED. THE MODEL AUTHORS NOTHING ON
 * THIS CARD.** That is the whole design and the alternative is the trap: a model writing a markdown
 * table is a model that will drop a column, round an hour, invent a status name or summarise five
 * rows as four — the fabrication C17 refused, wearing a table's clothing. So the tool reads through
 * the ordinary service, the rows cross as the product's own wire type, and this renders them with
 * the product's own list.
 *
 * ⚠️ **Rendered with `taskRows`/`taskColumns` — the tasks screen's own mapping**, per CLAUDE.md's
 * frontend rule: *"if a screen wants a `TaskTable`, it wants the list component with task columns."*
 * A chat-specific table would be a second place for every colour and formatter to drift, and the
 * drift would be invisible: `8.0` here where the list says `8:00`.
 *
 * ⚠️⚠️ **A CARD IS A SNAPSHOT AND SAYS WHEN IT WAS READ.** `/agent`'s `notLiveBecause` is an argument
 * about this component: *a reply is a record of what was read at a moment, and the prose beside a
 * figure cannot be re-derived when the figure changes.* So no card subscribes to anything, every card
 * stamps its `readAt`, and `debt.md` holds the row for the one affordance that would be honest — a
 * quiet *"changed since"* marker on the latest block only.
 *
 * ⚠️ **The geometry is a DEPARTURE FROM A DRAWN SCREEN and it is flagged, not absorbed.**
 * `markup.html` 1534–1560 draws a two-column key/value strip inside a 680px thread; that is honest
 * for *"hours per project"* and hopeless for a task list. §4 proposes 1040px and five columns, and
 * `decisions.md` carries it as an open question for the product owner. What is implemented here
 * differs from the plan in one respect, argued in `agent-screen.tsx`: the card is **centred on the
 * thread's own axis** rather than aligned to its left edge, because a centred 680px prose column and
 * a left-aligned 1040px card cannot both hold.
 */

export interface AgentDataCardProps {
  readonly block: AgentDataBlock;
  /** ⚠️ A KEY, because `/tasks/:key` is the address people paste. See the note at the call. */
  readonly onOpenTask: (key: string) => void;
}

export function AgentDataCard({ block, onOpenTask }: AgentDataCardProps): ReactNode {
  const t = useT();

  return (
    <section
      /* ⚠️ A landmark with a name, so a screen reader can be told *what* the table under the reply
         is. `aria-label` rather than a visible heading: the header row below already names it, and
         two names for one region is what makes a transcript unreadable aloud. */
      aria-label={kindLabel(t, block)}
      data-agent-data={block.kind}
      className="bg-card text-card-foreground overflow-hidden rounded-xl border shadow-sm"
    >
      <div className="flex items-center gap-2 border-b px-3.5 py-2">
        <span className="text-[12.5px] font-medium">{kindLabel(t, block)}</span>
        <div className="flex-1" />
        <ReadAt at={block.readAt} />
      </div>

      {block.kind === 'worklog_overview' ? (
        <Overview block={block} />
      ) : (
        <Table block={block} onOpenTask={onOpenTask} />
      )}
    </section>
  );
}

/** ⚠️ One `t()` per branch with a literal key — a computed key is invisible to `i18n:check`. */
function kindLabel(t: Translate, block: AgentDataBlock): string {
  if (block.kind === 'tasks') return t('agent.data.tasks');
  if (block.kind === 'worklogs') return t('agent.data.worklogs');
  return t('agent.data.overview');
}

/**
 * When the rows were read.
 *
 * ⚠️⚠️ **THE STAMP IS THE HALF OF R-26 A PERSON CAN SEE.** A table in a transcript that carries no
 * time is a table somebody will read as current — and the sentence above it was written about the
 * moment it was read, not about now.
 *
 * ⚠️ **Formatted in the reader's locale from an instant, which is the one place in this feature that
 * is correct**: `readAt` is a moment (when the read happened), unlike a `workedOn`, which is a
 * calendar day. `hard-won.md`'s driver rows are about not confusing the two.
 */
function ReadAt({ at }: { readonly at: string }): ReactNode {
  const { locale } = useI18n();
  const t = useT();
  const parsed = new Date(at);
  const label = Number.isNaN(parsed.getTime())
    ? at
    : parsed.toLocaleTimeString(locale === 'hu' ? 'hu-HU' : 'en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      });

  return (
    <span className="text-muted-foreground text-[11px] tabular-nums">
      {t('agent.data.readAt', { at: label })}
    </span>
  );
}

/**
 * The footer note.
 *
 * ⚠️⚠️ **A CAP IS STATED, NEVER SILENT — `state-rules.md` §5.** Three cases and they are different
 * sentences, not one sentence with a number in it:
 *
 *  * cut, and the true total is known → *"25 of 312 · refine the question"*;
 *  * cut, and nobody has counted → *"the first 25"*, because the worklog list pages and
 *    `pagination.ts` argues why no envelope carries a count. A ratio here would be invented;
 *  * complete → the count, plainly.
 */
function footer(
  t: Translate,
  block: Extract<AgentDataBlock, { kind: 'tasks' | 'worklogs' }>,
  shown: number,
): string {
  if (!block.truncated) {
    return block.kind === 'tasks'
      ? t('tasks.footer', { count: shown })
      : t('logs.footer', { count: shown });
  }
  if (block.total === null) return t('agent.data.first', { count: shown });
  return t('agent.data.of', { shown, total: block.total });
}

function Table({
  block,
  onOpenTask,
}: {
  readonly block: Extract<AgentDataBlock, { kind: 'tasks' | 'worklogs' }>;
  readonly onOpenTask: (key: string) => void;
}): ReactNode {
  const t = useT();
  const { fmt } = useDuration();
  /* ⚠️ Asked for ONCE for the whole card — `use-avatar.ts`: twelve rows assigned to three people is
   * three requests, not twelve. Called unconditionally because a hook cannot be conditional; a
   * worklog card passes an empty list. */
  const avatars = useAvatars(
    block.kind === 'tasks' ? block.items.map((task) => task.assigneeAvatarUrl) : [],
  );

  const built =
    block.kind === 'tasks'
      ? taskRows(t, block.items, avatars, [], fmt)
      : worklogRows(t, block.items, fmt);
  const columns = (
    block.kind === 'tasks'
      ? taskColumns(t, CHAT_TASK_COLUMNS)
      : worklogColumns(t, CHAT_WORKLOG_COLUMNS)
  ).map((column) => ({ ...column, sortKey: column.key }));

  /**
   * ⚠️⚠️ **THE COLUMNS ARE WHATEVER THE TOOL RETURNED, SO THE READERS ARE TOO.** This card
   * draws tasks on one block and worklogs on the next; there is no one source type to read a key
   * off, so every column reads its own cell. `byCell` prefers a cell's `sort` to its text.
   *
   * ⚠️ **Sorting a SNAPSHOT is safe** in the way re-querying one would not be: the rows are the
   * evidence the answer was built from, and reordering them does not change which rows they are.
   */
  const [sort, setSort] = useState<readonly SortState[]>([]);

  const rows = sortInMemory(
    built,
    sort,
    Object.fromEntries(columns.map((column) => [column.key, byCell(column.key)])),
  );

  return (
    <>
      <DataList
        columns={columns}
        sort={sort}
        onSortChange={setSort}
        rows={rows}
        /**
         * ⚠️⚠️ **NO TOOLBAR AND NO SELECTION, AND BOTH ARE ARGUED IN `data-list.tsx`.** A search box
         * above a snapshot would offer to re-query it; a column picker would offer to rearrange a
         * sentence's evidence; a checkbox per row with no bulk action is a control that does nothing.
         */
        toolbar={false}
        selectable={false}
        onRowClick={
          block.kind === 'tasks'
            ? (id) => {
                /**
                 * ⚠️⚠️ **THE PAYOFF OF DOING IT THIS WAY: the agent's answer is not a picture of a
                 * task list, it is a WORKING one.** A row opens the task.
                 *
                 * ⚠️ **It navigates to `/tasks/:key` rather than opening `task-overlay.tsx`**, which
                 * is where `docs/plan/agent.md` §4 pointed. The overlay needs the statuses, the
                 * priorities, the projects, the members, the permissions and a mutation path — it is
                 * the tasks route, effectively — and the key address exists precisely so a task can
                 * be opened from anywhere, including from a link somebody pastes into a chat.
                 */
                const task = block.items.find((candidate) => candidate.id === id);
                if (task !== undefined) onOpenTask(task.key);
              }
            : undefined
        }
        empty={{
          icon: block.kind === 'tasks' ? 'list-checks' : 'clock',
          title: t('agent.data.empty.title'),
          body: t('agent.data.empty.body'),
        }}
        footerNote={footer(t, block, rows.length)}
        labels={listLabels(t)}
        className="min-h-0"
      />

      {/**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ WHAT THIS TABLE NO LONGER CONTAINS, NAMED. R-26, and C27.4 is    │
       * │ where it becomes reachable.                                           │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * A reopened conversation re-reads the stored ids through the ordinary services, so a member
       * removed from a project since the reply was written gets a shorter table than the sentence
       * above it describes. *"Két sor már nem látszik"* is what makes the prose explicable; saying
       * nothing would make it look wrong. Always absent on a live card, where nothing was re-read.
       */}
      {block.missing > 0 ? (
        <p
          className="text-muted-foreground border-t px-3.5 py-1.5 text-[11px]"
          data-agent-missing={block.missing}
        >
          {t('agent.data.missing', { count: block.missing })}
        </p>
      ) : null}

      {/* ⚠️ **The dropped columns are NAMED**, the same rule as the truncated total: a table showing
          five of eight columns without saying so is a table somebody will read as the whole row. */}
      {block.kind === 'tasks' && block.items.length > 0 ? (
        <p className="text-muted-foreground border-t px-3.5 py-1.5 text-[11px]">
          {t('agent.data.tasks.dropped')}
        </p>
      ) : null}
    </>
  );
}

/**
 * The hours aggregate, read-only.
 *
 * ⚠️⚠️ **EVERY FIGURE ON THIS PANEL WAS SUMMED IN POSTGRES AND FORMATTED SERVER-SIDE, AND NOTHING
 * HERE ADDS ANYTHING.** `worklog.service.ts` states why: summing `numeric` in JavaScript
 * reintroduces floating-point drift on the figures somebody bills from, and formatting per call site
 * is how one screen shows `7.5` and the next `7:30`.
 *
 * ⚠️ **A STRIP, NOT THE `/time` CONTROL.** The overview screen's strip is seven buttons that select a
 * day and shift a week; a card in a transcript selects nothing — it is the record of one week that
 * was read. Extracting that control to serve a static caller would make an interactive component
 * prop-driven for one reader who presses nothing. What IS shared is the part that could drift: the
 * weekday labels and the day numbers (`week.ts`), which were already duplicated across two screens
 * before this card wanted them.
 */
function Overview({
  block,
}: {
  readonly block: Extract<AgentDataBlock, { kind: 'worklog_overview' }>;
}): ReactNode {
  const t = useT();
  const { fmt } = useDuration();

  return (
    <div className="p-3.5">
      <div className="flex items-center gap-2 pb-2">
        {/* ⚠️⚠️ **WHOSE HOURS, FIRST AND IN WORDS.** A week total that is silently the whole
            workspace's is a wrong answer with a chart under it — and the frame carries the subject as
            a machine value precisely so this sentence is the client's, in HU or EN. */}
        <span className="text-[12px] font-medium">
          {block.subject === 'own' ? t('agent.data.subject.own') : t('agent.data.subject.everyone')}
        </span>
        <div className="flex-1" />
        <span className="text-[12.5px] tabular-nums">{fmt(block.overview.weekHours)}</span>
      </div>

      <div className="flex items-stretch gap-1.5">
        {block.overview.days.map((day, index) => (
          /* ⚠️ The full date as a tooltip, because `17.` alone is not a date somebody can quote. */
          <Tooltip key={day.date} asChild content={day.date}>
            <div className="bg-muted/40 flex-1 rounded-lg px-1 py-1.5 text-center">
              <span className="text-muted-foreground block text-[10px] uppercase">
                {dowLabel(t, index)}
              </span>
              <span className="block text-[13px] font-medium">{dayOfMonth(day.date)}</span>
              {/* ⚠️ An en-dash for a day with NO entries, never `0:00` — the kit's rule, and *"nothing
                logged"* is not *"zero hours worked"*. `/time` draws the same distinction. */}
              <span className="text-muted-foreground block text-[11px] tabular-nums">
                {day.hasEntries ? fmt(day.hours) : '–'}
              </span>
            </div>
          </Tooltip>
        ))}
      </div>

      <div className="mt-3">
        {block.overview.byProject.map((share) => (
          <div key={share.projectId ?? 'none'} className="mb-2 last:mb-0">
            <div className="flex items-center gap-2 text-[12px]">
              <span className="flex-1 truncate">
                {share.projectId === null ? t('time.noProject') : share.name}
              </span>
              <span className="tabular-nums">{fmt(share.hours)}</span>
            </div>
            <Progress
              value={share.percent}
              label={share.projectId === null ? t('time.noProject') : share.name}
              {...(share.color === null ? {} : { color: share.color })}
              className="mt-1"
            />
          </div>
        ))}
        {block.overview.byProject.length === 0 ? (
          /* ⚠️ **Not `time.week.empty`, whose Hungarian says *"nincs rögzítésed"* — YOUR hours.** This
           * card may be about the whole workspace, and a possessive that is wrong half the time is
           * worse than a neutral sentence. */
          <p className="text-muted-foreground text-[12px]">{t('agent.data.noHours')}</p>
        ) : null}
      </div>
    </div>
  );
}
